import React from 'react';
import {inject, observer} from 'mobx-react';
import {TechseeBaseComponent} from '@techsee/techsee-ui-common/lib/_shared/techsee-base-component';
import {IBrowserNotSupportedController} from '../../browser-not-supported.controller';
import {IBrowserNotSupportedStore} from '../../browser-not-supported.stores';
import {resolveLogoUrl} from '../../../../utils/logo-url.utils';

import './styles.scss';

interface LayoutProps {
    browserNotSupportedController: IBrowserNotSupportedController;
}

const mapStoresToProps = (stores: {browserNotSupportedStore: IBrowserNotSupportedStore}): LayoutProps => ({
    browserNotSupportedController: stores.browserNotSupportedStore.browserNotSupportedController
});

const requireImage = (imageName: string) => `/img/${imageName}`;

@inject(mapStoresToProps)
@observer
export class BrowserNotSupportedLayoutComponent extends TechseeBaseComponent<LayoutProps> {
    private renderBrowserIcon(browserName: string): JSX.Element | null {
        const iconMap: {[key: string]: string} = {
            Safari: 'safari-icon.png',
            'Mobile Safari': 'safari-icon.png',
            'Safari Mobile': 'safari-web-icon.png',
            Firefox: 'firefox-icon.png',
            'Chrome Webview': 'chrome-web-icon.png',
            Chrome: 'chrome-icon.png',
            'Chrome for Android': 'chrome-icon.png',
            'Chrome for iOS': 'chrome-icon.png',
            Edge: 'edge-icon.png',
            IE: 'IE11-icon.png'
        };

        const iconName = iconMap[browserName];
        if (!iconName) return null;

        return <img key={browserName} className='browser-img' src={requireImage(iconName)} alt={browserName} />;
    }

    private renderGeneralNotSupported(): JSX.Element {
        const controller = this.props.browserNotSupportedController;

        return (
            <div className='ts-main'>
                <main>
                    <div className='ts-browser-not-supported' dir={controller.dir}>
                        <div className='ts-browser-not-supported-image'>
                            <img className='logo' src={resolveLogoUrl('')} alt='TechSee Logo' />
                        </div>

                        <div className='ts-browser-not-supported-image'>
                            <img
                                className='unsupported-image'
                                src={requireImage('unsupported-br.png')}
                                alt='Unsupported Browser'
                            />
                        </div>

                        <h2 className='ts-browser-not-supported-message'>
                            {controller.translate(
                                'BROWSER_NOT_SUPPORTED.CONTROLLER.GENERAL_NOT_SUPPORTED_BY_THIS_BROWSER'
                            )}
                        </h2>

                        {controller.browserInfo?.isSupportedBrowser && (
                            <h4 className='ts-browser-not-supported-sub-message'>
                                {controller.translate('BROWSER_NOT_SUPPORTED.CONTROLLER.LOGIN_VIA_SUPPORTED_VERSION', {
                                    version: controller.browserMajor
                                })}
                            </h4>
                        )}

                        {!controller.browserInfo?.isSupportedBrowser && (
                            <h4 className='ts-browser-not-supported-sub-message'>
                                {controller.translate(
                                    'BROWSER_NOT_SUPPORTED.CONTROLLER.LOGIN_VIA_ONE_OF_THE_FOLLOWING_BROWSERS'
                                )}
                            </h4>
                        )}

                        <div className='ts-browser-not-supported-message'>
                            <img className='browser-img' src={requireImage('safari-icon.png')} alt='Safari' />
                            <img
                                className='browser-img'
                                src={requireImage('safari-web-icon.png')}
                                alt='Safari Mobile'
                            />
                            <img className='browser-img' src={requireImage('firefox-icon.png')} alt='Firefox' />
                            <img
                                className='browser-img'
                                src={requireImage('chrome-web-icon.png')}
                                alt='Chrome Webview'
                            />
                            <img className='browser-img' src={requireImage('chrome-icon.png')} alt='Chrome' />
                            <img className='browser-img' src={requireImage('edge-icon.png')} alt='Edge' />
                            <img className='browser-img' src={requireImage('IE11-icon.png')} alt='IE' />
                        </div>
                    </div>
                </main>
            </div>
        );
    }

    private renderAccountSpecificNotSupported(): JSX.Element {
        const controller = this.props.browserNotSupportedController;
        const browserInfo = controller.browserInfo;

        return (
            <div className='ts-main'>
                <main>
                    <div className='ts-browser-not-supported' dir={controller.dir}>
                        <div className='ts-browser-not-supported-image'>
                            <img className='logo' src={resolveLogoUrl('')} alt='TechSee Logo' />
                        </div>

                        <div className='ts-browser-not-supported-image'>
                            <img
                                className='unsupported-image'
                                src={requireImage('unsupported-br.png')}
                                alt='Unsupported Browser'
                            />
                        </div>

                        <h2 className='ts-browser-not-supported-message'>
                            {controller.translate(
                                'BROWSER_NOT_SUPPORTED.CONTROLLER.GENERAL_NOT_SUPPORTED_BY_THIS_BROWSER'
                            )}
                        </h2>

                        {/* Browser not supported */}
                        {browserInfo && !browserInfo.isSupportedBrowser && (
                            <h3 className='not-supported-sub-message'>
                                {controller.translate('BROWSER_NOT_SUPPORTED.VIEW.ENABLED_BROWSERS', {
                                    enabledBrowsers: controller.enabledBrowsers
                                })}
                            </h3>
                        )}

                        {/* Smaller than minimum version */}
                        {browserInfo?.isOldVersion && (
                            <h3 className='not-supported-sub-message'>
                                {controller.translate('BROWSER_NOT_SUPPORTED.VIEW.MINIMUM_VERSION_BROWSER', {
                                    browserName: browserInfo.browserName as string,
                                    minVersion: controller.minVersion as string
                                })}
                            </h3>
                        )}

                        {/* Excluded version */}
                        {browserInfo?.isExcludedVersion && (
                            <h3 className='not-supported-sub-message'>
                                {controller.translate('BROWSER_NOT_SUPPORTED.VIEW.EXCLUDED_VERSION_BROWSER', {
                                    browserName: browserInfo.browserName,
                                    excludedVersions: controller.excludedVersions as string,
                                    minVersion: controller.minVersion as string
                                })}
                            </h3>
                        )}

                        <div className='ts-browser-not-supported-message'>
                            {controller.browserIcons.map((browserName) => this.renderBrowserIcon(browserName))}
                        </div>
                    </div>
                </main>
            </div>
        );
    }

    renderInternal(): JSX.Element {
        this.addCssClass('ts-browser-not-supported');
        this.addCssClass('ts-container');

        const controller = this.props.browserNotSupportedController;

        return (
            <div className={this.renderCssClasses()}>
                {!controller.userName ? this.renderGeneralNotSupported() : this.renderAccountSpecificNotSupported()}
            </div>
        );
    }
}

export default BrowserNotSupportedLayoutComponent;
